import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GroupWithMembers } from '@schooly/api';
import { SchoolPeriodLabel } from '@schooly/components/filters';
import { useRenderGroupType } from '@schooly/hooks/use-render-group-type';
import {
  ArchiveIcon,
  CrossIcon,
  EditIcon,
  LockIcon,
  TypographyWithOverflowHint,
} from '@schooly/style';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { LongTitleVerticalTooltip } from '../../../components/uikit/LongTitleVerticalTooltip/LongTitleVerticalTooltip';
import { ModalHeaderV2 } from '../../../components/uikit-components/Modal/ModalHeaderV2';
import { AgeGroupLabel } from '../AgeGroupLabel';
import { GroupPreviewModalCloneButton } from './GroupPreviewModalCloneButton';

interface EditableProps {
  onEditClick: (property?: keyof GroupWithMembers) => void;
  editable?: boolean;
}

export interface ViewGroupModalInfoProps extends EditableProps {
  group?: GroupWithMembers;
  isExpired?: boolean;
  validity?: string;
  onModalClose: VoidFunction;
  active?: boolean;
  showEditButton?: boolean;
  showCloneButton?: boolean;
  withGoBack?: boolean;
  periodLabel?: string;
}

export const GroupPreviewModalInfoV2: React.FC<ViewGroupModalInfoProps> = ({
  group,
  validity = '-',
  isExpired,
  onModalClose,
  showEditButton,
  onEditClick,
  active,
  showCloneButton,
  withGoBack,
  periodLabel,
}) => {
  const { formatMessage } = useIntl();

  const renderGroupType = useRenderGroupType();

  const groupType = renderGroupType(group);
  const sortedAgeGroups = useMemo(
    () => [...(group?.age_groups ?? [])].sort((a, b) => a.order - b.order),
    [group?.age_groups],
  );

  const subHeaderContent = useMemo(
    () => (
      <Stack gap={5} direction="row" alignItems="baseline" overflow="hidden">
        <Box sx={{ whiteSpace: 'nowrap' }}>
          {periodLabel ? (
            <SchoolPeriodLabel label={periodLabel} dateRange={validity} />
          ) : (
            <Typography color="text.primary">{validity}</Typography>
          )}
        </Box>
        {!!sortedAgeGroups.length && (
          <TypographyWithOverflowHint color="text.primary" noWrap maxWidth="50%">
            {sortedAgeGroups.map((ag, i) => (
              <React.Fragment key={ag.id}>
                {i > 0 && ', '}
                <AgeGroupLabel ageGroup={ag} />
              </React.Fragment>
            ))}
          </TypographyWithOverflowHint>
        )}
        {!!group?.description && (
          <TypographyWithOverflowHint color="text.primary" noWrap maxWidth="50%">
            {group.description}
          </TypographyWithOverflowHint>
        )}
      </Stack>
    ),
    [sortedAgeGroups, group?.description, periodLabel, validity],
  );

  const actionsContent = useMemo(
    () => (
      <>
        {!!groupType && (
          <Box
            sx={(theme) => ({
              px: 1.75,
              py: 0.25,
              whiteSpace: 'nowrap',
              border: theme.mixins.border(),
              borderRadius: theme.spacing(0.5),
            })}
          >
            {group?.subject?.archived ? (
              <Tooltip title={formatMessage({ id: 'schoolProperty-Archived-subject' })}>
                <Typography variant="h3" color="text.primary">
                  <ArchiveIcon /> {groupType}
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant="h3" color="text.primary">
                {groupType}
              </Typography>
            )}
          </Box>
        )}

        {showEditButton &&
          (isExpired ? (
            <Tooltip title={formatMessage({ id: 'groups-NoModifyExpiredGroup' })}>
              <IconButton sx={{ cursor: 'default !important' }}>
                <LockIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton onClick={() => onEditClick()} data-test-id="modal-edit">
              <EditIcon />
            </IconButton>
          ))}
        {showCloneButton && <GroupPreviewModalCloneButton group={group} />}
        <IconButton data-test-id="modal-close" onClick={onModalClose}>
          <CrossIcon />
        </IconButton>
      </>
    ),
    [
      groupType,
      group,
      formatMessage,
      showEditButton,
      isExpired,
      showCloneButton,
      onModalClose,
      onEditClick,
    ],
  );

  return (
    <Box>
      <ModalHeaderV2
        active={active}
        title={
          <LongTitleVerticalTooltip
            renderTooltipContent={(longName) => <Typography variant="h2">{longName}</Typography>}
            withoutArrow
            getOffset={(height) => [-8, -(height + 12)]}
          >
            {group?.name}
          </LongTitleVerticalTooltip>
        }
        withGoBack={withGoBack}
        subHeader={subHeaderContent}
      >
        {actionsContent}
      </ModalHeaderV2>
    </Box>
  );
};
